import { getStore } from 'app/hermes-redux';
import { getRouteUrlFromStateName, isPath } from 'router/helpers';
import { injectParamsInUrl } from '../globals/helpers';
import { isFeatureEnabled } from 'stores/features-list-store';
import { Module } from 'router/types';
import GoogleAnalytics from 'services/analytics/analytics-engine';
import { getGASectionFromUrl } from 'services/analytics/helpers';
import ManulifeEngine from 'services/analytics/manulife-analytics-engine';
import { createBrowserHistory } from 'history';
import queryString from 'query-string';
import { getRouterState } from './router-state-provider';
import { isManulifeOriginator } from 'globals/manulife';
import * as Storage from 'globals/storage';
import { isUserLoggedIn } from 'state/session/helpers';
import { showHotjarSurvey } from 'globals/hotjar/helpers';

export const history = createBrowserHistory();

export function getCurrentState() {
  return getRouterState().currentRoute;
}

export function getPreviousState() {
  return getRouterState().previousRoute;
}

export const trackRouteChange = (newRoute: Module) => {
  showHotjarSurvey(newRoute);

  GoogleAnalytics.trackScreenView(
    newRoute.url,
    getGASectionFromUrl(newRoute.url),
    newRoute.params,
  );
  ManulifeEngine.setPage(newRoute.url);
};

export const getQueryParams = (search?: string): Record<string, any> => {
  return queryString.parse(search ?? location.search);
};

// Used only for manulife
export function getLandingStateFromFeaturesList() {
  // order matters here
  switch (true) {
    case isFeatureEnabled('care_plans'):
    case isFeatureEnabled('counsellor_appointments'):
    case isFeatureEnabled('care_coordination_specialists_messaging'):
      return 'myCareHub';
    case isFeatureEnabled('social_feed'):
      return 'feed';
    case isFeatureEnabled('eap_assistance'):
      return 'employeeAssistanceHome';
    case isFeatureEnabled('benefit_online_shop'):
      return 'shop';
    case isFeatureEnabled('benefit_colleague_offer'):
      return 'colleagueOffers';
    case isFeatureEnabled('benefit_restaurant'):
      return 'restaurants';
    case isFeatureEnabled('benefit_in_store_offer'):
      return 'instoreOffers';
    case isFeatureEnabled('benefit_global_gift_cards'):
      return 'giftcards';
    case isFeatureEnabled('social_colleague_directory'):
      return 'directory';
    case isFeatureEnabled('social_recognition'):
      return 'leaderboard';
    case isFeatureEnabled('eap_hwr_toolkits'):
      return 'hwrHome';
    default:
      return 'profile';
  }
}

// This function should not be used directly to find the landing URL
// Instead use the wrapper function getLandingPageUrl which handles special cases
export function getLandingState(): string {
  if (isManulifeOriginator()) {
    return getLandingStateFromFeaturesList();
  }

  return 'home';
}

// quite dumb for now, but good enough, do we need to take into accounts params?
// TODO: LOGIN REFACTOR
// API should return return the actual route and we should then use it directly.
export function getStateFromUrl(apiState: string): string {
  const stateMapping = {
    'wallet/transactions': 'wallet',
    wallet: 'wallet',
    profile: 'profile',
    directory: 'directory',
    leaderboard: 'leaderboard',
    feed: 'feed',
    'shop/retailers': 'shop',
    'shop/retailers/category': 'shop',
    'shop/products/compare': 'shop',
    shop: 'shop',
    'eap/employee': 'employeeAssistanceHome',
    'life/hwr': 'hwrHome',
    '/employee-assistance': 'employeeAssistanceHome',
  };

  return Object.keys(stateMapping)
    .filter(k => apiState.indexOf(k) > -1)
    .map(k => stateMapping[k])[0];
}

export function getLandingPageUrl(isSessionInvalidating?: boolean) {
  const { user } = getStore().getState().session;
  const previousState = getPreviousState();
  const { isComingFromNative } = getStore().getState().global || {};

  if (
    previousState?.name?.startsWith('employeeAssistance') &&
    isComingFromNative
  ) {
    return getRouteUrlFromStateName('employeeAssistanceHome');
  }

  if (user && !isSessionInvalidating) {
    const landingState = getLandingState();

    return getRouteUrlFromStateName(landingState);
  }
}

export function goTo(
  route,
  params?: Record<string, any>,
  {
    reload,
    shouldNotPushToHistory,
    noUpdate,
    stateParams,
  }: { reload?; shouldNotPushToHistory?; noUpdate?; stateParams? } = {},
) {
  const destinationUrl = isPath(route)
    ? injectParamsInUrl(route, params)
    : getUrl(route, params);

  if (reload) {
    window.location.assign(destinationUrl); // hard reload
  } else if (noUpdate) {
    window.history.replaceState({}, '', destinationUrl);
  } else {
    goToUrl(destinationUrl, shouldNotPushToHistory, {
      ...params,
      ...stateParams,
    }); // normal page transition
  }
}

export function goToUrl(
  path: string,
  shouldNotPushToHistory?: boolean,
  params?: Record<string, any>,
) {
  if (shouldNotPushToHistory) {
    if (isUserLoggedIn()) {
      Storage.remove(Storage.storageKeys.originalPath);
    }
    // getAndClearOriginalPath() runs after any top level navigation and will restore the original path if it exists
    // to allow users to follow a link, then login or sign up, and be redirected to the original link they were trying to access
    // however in some cases we do not want this to happen
    // for example if the user is redirected to a /404 "not found" page, they need to stay there, not be bounced back to the invalid path they started at
    // so in these cases we clear the originalPath from storage
    // it might be cleaner to check for restrictions in getAndClearOriginalPath()
    // however restrictions rely on router state which is not available in the getAndClearOriginalPath helper function
    // so we are using a pre-existing flag, shouldNotPushToHistory, which indicates that we do not want to push to history
    // however, we need to make sure we don't lose originalPath if the user is in the middle of logging in and will get access to the route after login or signup
    // and should not redirect to an original path
    history.replace(path, params);
  } else {
    history.push(path, params);
  }

  // reset scroll position
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

export function getUrlParameter(): Record<string, any>;
export function getUrlParameter(input: string): string;

export function getUrlParameter(param?: string) {
  // Params are set to currentRoute state when navigating to a new route
  const params = getRouterState().currentRoute?.params ?? {};

  const result = param !== undefined ? params[param] : params;

  return result ?? '';
}

export function getUrl(stateName: string, params?: Record<string, any>) {
  const routeUrl = getRouteUrlFromStateName(stateName, params);

  if (routeUrl) {
    return history.createHref(routeUrl);
  }

  return '/404';
}

export const createHref = routePath => history.createHref(routePath);

export function getSubdomainFromHost() {
  const host = window.location.hostname;

  return host.split('.')[0] as string;
}

/**
 * Returns the portion of the current URL (including port, path, and query string) starting from the first dot in the hostname.
 *
 * For example:
 *   "https://app.localhost:3000/?code=56e" => ".localhost:3000/?code=56e"
 *   "https://company.test.one.telushealth.com/" => ".test.one.telushealth.com/"
 *
 * Note: This does NOT guarantee the result is a root domain; it simply returns everything after the first dot in the hostname.
 * This is useful for reconstructing URLs for subdomain switching, preserving the rest of the domain and any URL parameters.
 */
export const getDomainSuffixAndPathFromHref = (): string => {
  const href = window.location.href;
  return href.slice(href.indexOf('.'));
};

/**
 * Returns the portion of the current host (hostname and port) starting from the first dot.
 *
 * For example:
 *   "app.localhost:3000" => ".localhost:3000"
 *   "company.test.one.telushealth.com" => ".test.one.telushealth.com"
 *
 * Note: This does NOT guarantee the result is a root domain; it simply returns everything after the first dot in the host.
 * Use this for subdomain manipulations where you want to preserve the rest of the domain and port.
 */
export const getDomainSuffixFromHost = () => {
  const host = window.location.host;
  return host.slice(host.indexOf('.'));
};

export function goBack() {
  history.back();
}
